import {
  createListenerMiddleware,
  addListener,
  type TypedStartListening,
  type TypedAddListener
} from "@reduxjs/toolkit";

import { getIntl } from "Libs/intl";

import type { RootState, AppDispatch } from "./configureStore";

/**
 * Listener middleware for Redux Toolkit.
 *
 * Use this instead of useEffect for async coordination:
 * - Reacting to action dispatches
 * - Coordinating async flows (fetch A -> fetch B)
 * - Auto-selecting options based on state changes
 *
 * Benefits over useEffect:
 * - cancelActiveListeners() prevents race conditions
 * - condition() waits for prerequisite state
 * - Centralized async logic, not scattered in components
 *
 * @see https://redux-toolkit.js.org/api/createListenerMiddleware
 */
export const listenerMiddleware = createListenerMiddleware({
  extra: { getIntl }
});

/**
 * Typed version of startListening for this app.
 * Use this to register listeners with proper typing.
 */
export type AppStartListening = TypedStartListening<
  RootState,
  AppDispatch,
  { getIntl: typeof getIntl }
>;

export const startAppListening =
  listenerMiddleware.startListening as AppStartListening;

/**
 * Typed version of addListener for dynamic listener registration.
 */
export type AppAddListener = TypedAddListener<
  RootState,
  AppDispatch,
  { getIntl: typeof getIntl }
>;

export const addAppListener = addListener as AppAddListener;

/**
 * Stop a listener by its effect reference.
 */
export const stopAppListening = listenerMiddleware.stopListening;
